import { useState } from 'react';
import { motion } from 'framer-motion';
import { usePageTitle } from '../../hooks/usePageTitle';
import { 
  Section, 
  Card, 
  FadeInUp,
  SlideIn,
} from '../../components/common';

const WHATSAPP_NUMBER = '5581997160139';

export default function Contact() {
  usePageTitle('Contato');
  const [message, setMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      const encodedMessage = encodeURIComponent(message);
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`, '_blank');
    }
  };

  const contactOptions = [
    {
      icon: 'ri-whatsapp-line',
      title: 'WhatsApp',
      value: '+55 81 9716-0139',
      link: `https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de saber mais sobre as aulas de inglês da Beyond Class.`,
      description: 'Resposta rápida',
      color: 'bg-green-50',
      iconColor: 'text-green-600',
    },
    {
      icon: 'ri-instagram-line',
      title: 'Instagram',
      value: '@beyondclass_eas',
      link: 'https://instagram.com/beyondclass_eas',
      description: 'Acompanhe nosso dia a dia',
      color: 'bg-pink-50',
      iconColor: 'text-pink-600',
    },
  ];

  const quickMessages = [
    'Olá! Gostaria de saber mais sobre as aulas particulares.',
    'Olá! Tenho interesse nas turmas em grupo. Quais horários disponíveis?',
    'Olá! Quero informações sobre o curso online.',
    'Olá! Gostaria de agendar uma aula experimental.',
  ];

  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[32vh] sm:min-h-[40vh] flex items-center justify-center bg-earth-800">
        <div className="absolute inset-0 bg-gradient-to-br from-earth-900 to-earth-700 opacity-90" />
        
        <div className="container-custom relative z-10 pt-20 sm:pt-24 pb-8 sm:pb-12 text-center">
          <FadeInUp>
            <p className="text-olive-400 font-medium mb-2 sm:mb-4 flex items-center justify-center gap-2 text-sm">
              <i className="ri-chat-3-line text-base sm:text-lg"></i>
              Vamos conversar?
            </p>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-sand-100 mb-3 sm:mb-6">
              Entre em Contato
            </h1>
            <p className="text-sm sm:text-lg text-sand-300 max-w-2xl mx-auto leading-relaxed">
              Sua jornada de aprendizado começa com uma simples mensagem. 
              Estamos aqui para responder suas dúvidas e ajudar você a escolher 
              o melhor caminho para aprender inglês.
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Contact Form & Info */}
      <Section bgColor="sand">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Formulário Simplificado */}
            <SlideIn direction="left">
              <Card hover={false} className="h-full">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-earth-800 mb-2 flex items-center gap-2">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-olive-50 rounded-lg flex items-center justify-center">
                    <i className="ri-message-3-line text-olive-600 text-base sm:text-lg"></i>
                  </div>
                  Envie sua mensagem
                </h2>
                <p className="text-earth-500 text-xs sm:text-sm mb-4 sm:mb-6">
                  Escreva sua mensagem abaixo e ela será enviada diretamente para nosso WhatsApp.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                  <div>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      required
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-sand-300 rounded-xl focus:ring-2 focus:ring-olive-400 focus:border-olive-400 transition-all resize-none text-earth-800 text-sm sm:text-base"
                      placeholder="Olá! Gostaria de saber mais sobre..."
                    />
                  </div>

                  <motion.button
                    type="submit"
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 sm:py-4 rounded-xl transition-colors text-sm sm:text-base"
                  >
                    <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
                    Enviar pelo WhatsApp
                  </motion.button>
                </form>

                {/* Mensagens Rápidas */}
                <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-sand-200">
                  <p className="text-earth-500 text-xs sm:text-sm mb-2 sm:mb-3">Mensagens rápidas:</p>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {quickMessages.map((msg, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setMessage(msg)}
                        className="text-[10px] sm:text-xs bg-sand-100 hover:bg-olive-100 text-earth-600 hover:text-olive-700 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full transition-colors"
                      >
                        {msg.substring(0, 35)}...
                      </button>
                    ))}
                  </div>
                </div>
              </Card>
            </SlideIn>

            {/* Contatos Diretos */}
            <SlideIn direction="right" className="space-y-4 sm:space-y-6">
              {/* Opções de Contato */}
              <div>
                <h2 className="text-lg sm:text-xl md:text-2xl text-earth-800 mb-3 sm:mb-4 flex items-center gap-2">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-olive-50 rounded-lg flex items-center justify-center">
                    <i className="ri-phone-line text-olive-600 text-base sm:text-lg"></i>
                  </div>
                  Contato direto
                </h2>
                
                <div className="space-y-2 sm:space-y-3">
                  {contactOptions.map((option) => (
                    <a
                      key={option.title}
                      href={option.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-xl border border-sand-200 hover:shadow-md hover:-translate-y-1 transition-all"
                    >
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 ${option.color} rounded-lg flex items-center justify-center`}>
                        <i className={`${option.icon} text-lg sm:text-xl ${option.iconColor}`}></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-earth-800 text-sm sm:text-base">{option.title}</h3>
                        <p className="text-olive-600 font-medium text-xs sm:text-sm">{option.value}</p>
                        <p className="text-earth-500 text-[10px] sm:text-xs">{option.description}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* FAQ Simplificado */}
              <Card>
                <h3 className="text-base sm:text-lg font-semibold text-earth-800 mb-3 sm:mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 bg-olive-50 rounded-lg flex items-center justify-center">
                    <i className="ri-question-line text-olive-600 text-sm sm:text-base"></i>
                  </div>
                  Perguntas frequentes
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      q: 'Como funciona a aula experimental?',
                      a: 'Entre em contato pelo WhatsApp e agendaremos uma conversa gratuita para conhecer seus objetivos.',
                    },
                    {
                      q: 'As aulas são online ou presenciais?',
                      a: 'Oferecemos aulas online via Meet para alunos de qualquer lugar.',
                    },
                    {
                      q: 'Qual o valor das aulas?',
                      a: 'O valor varia conforme a modalidade escolhida. Entre em contato para um orçamento personalizado.',
                    },
                  ].map((item, index) => (
                    <div key={index} className="border-b border-sand-200 pb-4 last:border-0 last:pb-0">
                      <h4 className="font-medium text-earth-700 text-sm">{item.q}</h4>
                      <p className="text-earth-500 text-sm mt-1">{item.a}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </SlideIn>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section bgColor="dark">
        <FadeInUp className="text-center max-w-3xl mx-auto px-4 sm:px-0">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-sand-100 mb-3 sm:mb-4">
            Prefere uma resposta rápida?
          </h2>
          <p className="text-sand-300 text-base sm:text-lg mb-6 sm:mb-8 leading-relaxed">
            Fale diretamente conosco pelo WhatsApp e receba um atendimento personalizado.
          </p>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de saber mais sobre as aulas de inglês da Beyond Class.`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-colors"
          >
            <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
            Chamar no WhatsApp
          </a>
        </FadeInUp>
      </Section>
    </>
  );
}
